// ============================================================
// definitions.nav.ts — Admin › Definitions descriptor
// ------------------------------------------------------------
// Component-free, data-only navigation descriptor for the Admin
// Definitions group: what Twist defines once and production uses —
// component definitions, device definitions (recipes) and firmware.
// Imports ONLY pure types/constants (@twist/ui-navigation types +
// @twist/core security primitives) so the registry stays a pure data graph.
//
// The group is Twist-admin only. The component and device definitions are
// the Manufacturing management pages (routes under /manufacturing, guarded
// by the Manufacturing management permission), so their items also require
// that permission: nav-visible ⊆ route-accessible. The Manufacturing hub
// itself only holds the production process (batches, orders, stock, ...).
// ============================================================
import { OrgPermissions, TWIST_ADMIN_ONLY } from '@twist/core';
import type { NavDescriptor } from '@twist/ui-navigation';

/** Twist admin with Manufacturing management: the recipe and component type editors. */
const MANUFACTURING_DEFINITIONS = {
  ...TWIST_ADMIN_ONLY,
  requireOrganization: true,
  orgPermissions: [OrgPermissions.ADMIN__Manufacturing_Read],
};

export const definitionsNav: NavDescriptor = {
  id: 'definitions',
  path: '/manufacturing/definitions',
  label: 'Definitions',
  icon: 'gearsDatabase',
  group: 'Admin',
  // Admin section = 40s; Definitions second (after Manufacturing hub).
  order: 41,
  surfaces: ['sidebar', 'palette'],
  security: TWIST_ADMIN_ONLY,
  children: [
    {
      id: 'definitions-components',
      path: '/manufacturing/component-types',
      label: 'Component definitions',
      group: 'Admin',
      surfaces: ['sidebar', 'palette'],
      security: MANUFACTURING_DEFINITIONS,
    },
    {
      id: 'definitions-devices',
      path: '/manufacturing/definitions',
      label: 'Device definitions',
      group: 'Admin',
      surfaces: ['sidebar', 'palette'],
      security: MANUFACTURING_DEFINITIONS,
    },
    {
      id: 'definitions-firmware',
      path: '/hardware-buildup/firmware',
      label: 'Firmware',
      group: 'Admin',
      surfaces: ['sidebar', 'palette'],
      security: TWIST_ADMIN_ONLY,
      // Routed tab strip of the firmware page (tw-page-header-tabs navId="definitions-firmware"),
      // like the installation tabs; paths are relative to the page.
      children: [
        { id: 'definitions-firmware-builds', path: 'builds', label: 'Firmware builds', surfaces: ['tabs', 'breadcrumb'], order: 1 },
        { id: 'definitions-firmware-boards', path: 'boards', label: 'Board configurations', surfaces: ['tabs', 'breadcrumb'], order: 2 },
      ],
    },
  ],
};
